import {
  ComboboxItem,
  ComboboxParsedItem,
  ComboboxProps,
  Input,
  InputWrapperProps,
  Loader,
  MultiSelect,
  PasswordInput,
  Select,
  SelectProps,
  Switch,
  SwitchProps,
  TextInput,
  TextInputProps,
  Textarea,
  TextareaProps,
  Tooltip,
  TooltipProps,
} from "@mantine/core";
import { DatePickerInput, DatePickerInputProps } from "@mantine/dates";
import { AnyFieldApi, ValidationError } from "@tanstack/react-form";
import React, { type JSX, useId } from "react";

import SvgIcon from "~/components/SvgIcon";
import SvgIconRaw from "~/components/SvgIconRaw";
import { useMultiSelectCountPlaceholder } from "~/hooks/useMultiSelectCountPlaceholder";
import { DateFormat, DateFormatServer } from "~/utils/Types";
import dayjs from "~/utils/dayjs";

import { earliest, getDateLimits, latest } from "./Form/dateLimits";
import { FilterOptionsState } from "./createFilterOptions";
import { useFieldContext } from "./formContexts";

/** Extract a display string from a ValidationError (string or StandardSchemaV1Issue). */
const errorToString = (error: ValidationError): string =>
  typeof error === "string"
    ? error
    : error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error);

/** Join validation errors into a single comma-separated string, or undefined if none. */
const getErrorMessage = (errors?: ValidationError[]): string | undefined => {
  if (!errors?.length) return undefined;
  return [...new Set(errors.map(errorToString))].join(", ");
};

type FieldWrapperProps = Omit<
  InputWrapperProps,
  "label" | "error" | "description" | "children" | "id"
> & {};

type FieldBase = {
  disabled?: boolean;
  formControlProps?: FieldWrapperProps;
  helperText?: string | JSX.Element;
  label?: string;
  placeholder?: string;
};

// Base wrapper component for consistent styling
const FieldWrapper = ({
  children,
  formControlProps,
  field,
  ...props
}: {
  field: AnyFieldApi;
  children: (fieldId: string) => JSX.Element;
} & FieldBase) => {
  const fieldId = useId();
  const showError =
    !!field.state.meta.errors.length && field.state.meta.isTouched;
  const wrapperProps = formControlProps ?? {};

  return (
    <Input.Wrapper
      id={fieldId}
      label={props.label}
      error={showError ? getErrorMessage(field.state.meta.errors) : undefined}
      description={showError ? undefined : props.helperText}
      inputWrapperOrder={["label", "input", "description", "error"]}
      {...wrapperProps}
    >
      {children(fieldId)}
    </Input.Wrapper>
  );
};

// Autocomplete component using context.
// Mantine Select/MultiSelect internally; the props surface keeps the MUI
// Autocomplete names the 20+ call sites already use (options/getOptionLabel/
// isOptionEqualToValue/filterOptions/groupBy/renderOption/multiple/...).
// Field values stay the ORIGINAL option values (objects or strings) — options
// are keyed by index internally and mapped back on change.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- callers pass varying option shapes, same as the previous MUI Autocomplete typing
type AutocompleteOption = any;

type AutocompleteProps = {
  autocompleteProps: {
    options: AutocompleteOption[];
    getOptionLabel?: (option: AutocompleteOption) => string;
    isOptionEqualToValue?: (
      option: AutocompleteOption,
      value: AutocompleteOption,
    ) => boolean;
    filterOptions?: (
      options: AutocompleteOption[],
      state: FilterOptionsState,
    ) => AutocompleteOption[];
    groupBy?: (option: AutocompleteOption) => string;
    renderOption?: (
      props: object,
      option: AutocompleteOption,
    ) => React.ReactNode;
    renderTags?: () => React.ReactNode;
    disableClearable?: boolean;
    // MUI parity: keep the single Select's dropdown open after picking an
    // option. Mantine's Select otherwise closes and fires onDropdownClose on
    // select, which cancels a controlled multi-stage flow (see handleChange).
    // Mantine MultiSelect already stays open on select, so this is a no-op there.
    disableCloseOnSelect?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    loading?: boolean;
    inputValue?: string;
    onInputChange?: (event: unknown, value: string) => void;
    // Controlled overrides — when provided they replace the field wiring,
    // matching how the MUI spread used to override the base props.
    value?: AutocompleteOption | AutocompleteOption[] | null;
    onChange?: (
      event: unknown,
      value: AutocompleteOption | AutocompleteOption[] | null,
      reason?: string,
    ) => void;
    onBlur?: () => void;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    // Forwarded to Mantine Select/MultiSelect: when false the dropdown stays
    // closed on focus (Mantine defaults openOnFocus to true, so an autoFocus'd
    // searchable Select otherwise pops its list open on mount).
    openOnFocus?: boolean;
    style?: React.CSSProperties;
    sx?: { minWidth?: number | string };
    inputRef?: React.Ref<HTMLInputElement>;
    // Forwarded to Mantine Select/MultiSelect — e.g. `withinPortal: false`
    // so the dropdown stays inside a scoped colour-scheme wrapper instead
    // of portalling to document.body.
    comboboxProps?: ComboboxProps;
  };
  autoFocus?: boolean;
  autocompleteShowSearchIcon?: boolean;
  loading?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
} & FieldBase;

function AutocompleteField({
  autocompleteProps,
  autoFocus,
  autocompleteShowSearchIcon,
  loading,
  size,
  ...props
}: AutocompleteProps) {
  const field = useFieldContext();
  const countPlaceholder = useMultiSelectCountPlaceholder();
  const {
    options,
    getOptionLabel,
    isOptionEqualToValue,
    filterOptions,
    groupBy,
    renderOption,
    renderTags,
    disableClearable,
    disableCloseOnSelect,
    multiple,
    disabled,
    loading: loadingProp,
    inputValue,
    onInputChange,
    value: valueOverride,
    onChange: onChangeOverride,
    onBlur: onBlurOverride,
    open,
    onOpen,
    onClose,
    openOnFocus,
    style,
    sx,
    inputRef,
    comboboxProps,
  } = autocompleteProps;

  const currentValue =
    valueOverride !== undefined ? valueOverride : field.state.value;
  // Multi-select pills are hidden app-wide (filter-style controls render
  // their own chip summaries below the input), so surface the selection
  // count in the placeholder instead of leaving the input looking empty.
  const selectedCount = multiple
    ? ((currentValue as AutocompleteOption[]) ?? []).length
    : 0;
  // disableCloseOnSelect parity: Mantine's single Select closes and fires
  // onDropdownClose right after a select, which for a controlled flow (e.g.
  // ImageComparison's trait→value stages) runs onClose and immediately undoes
  // the pick. Arm a one-shot flag on select so that close is swallowed rather
  // than propagated to onClose.
  const suppressCloseAfterSelectRef = React.useRef(false);
  const handleChange = (
    value: AutocompleteOption | AutocompleteOption[] | null,
  ) => {
    if (disableCloseOnSelect && !multiple) {
      suppressCloseAfterSelectRef.current = true;
    }
    return onChangeOverride
      ? onChangeOverride(null, value, value == null ? "clear" : "selectOption")
      : field.handleChange(value);
  };
  const handleBlur = onBlurOverride ?? field.handleBlur;

  const getLabel = React.useCallback(
    (option: AutocompleteOption): string =>
      option == null
        ? ""
        : getOptionLabel
          ? getOptionLabel(option)
          : String(option),
    [getOptionLabel],
  );
  // Default equality falls back to label match, unlike MUI's strict default:
  // options rebuilt by a refetch keep their labels, so a persisted selection
  // still resolves. The cost is that two options sharing a label resolve to
  // the first; pass isOptionEqualToValue where labels can repeat.
  const isEqual = React.useCallback(
    (option: AutocompleteOption, value: AutocompleteOption): boolean =>
      isOptionEqualToValue
        ? isOptionEqualToValue(option, value)
        : option === value || getLabel(option) === getLabel(value),
    [isOptionEqualToValue, getLabel],
  );

  const keyOf = (index: number) => String(index);
  const optionAt = (key: string | null) =>
    key == null ? null : options[Number(key)];

  // Build (optionally grouped) data keyed by option index.
  const data = React.useMemo(() => {
    const items = options.map((option: AutocompleteOption, index: number) => ({
      value: keyOf(index),
      label: getLabel(option),
    }));
    if (!groupBy) return items;
    const groups = new Map<string, typeof items>();
    options.forEach((option: AutocompleteOption, index: number) => {
      const group = groupBy(option) ?? "";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(items[index]);
    });
    return [...groups.entries()].map(([group, groupItems]) => ({
      group,
      items: groupItems,
    }));
  }, [options, getLabel, groupBy]);

  // MUI filterOptions compat: filter over the original options, keep matches.
  const filter = React.useMemo(() => {
    if (!filterOptions) return undefined;
    return ({
      options: parsed,
      search,
    }: {
      options: ComboboxParsedItem[];
      search: string;
    }): ComboboxParsedItem[] => {
      const kept = new Set(
        filterOptions(options, {
          inputValue: search,
          getOptionLabel: getLabel,
          // Match through isEqual, not indexOf: a filter that returns fresh
          // objects would otherwise keep nothing.
        }).map((kept: AutocompleteOption) =>
          keyOf(
            options.findIndex((option: AutocompleteOption) =>
              isEqual(option, kept),
            ),
          ),
        ),
      );
      const keepItem = (item: ComboboxItem) => kept.has(item.value);
      return parsed
        .map((item) =>
          "group" in item
            ? { ...item, items: item.items.filter(keepItem) }
            : item,
        )
        .filter((item) =>
          "group" in item
            ? item.items.length > 0
            : keepItem(item as ComboboxItem),
        );
    };
  }, [filterOptions, options, getLabel, isEqual]);

  const renderOptionMantine = renderOption
    ? ({ option }: { option: ComboboxItem }) =>
        renderOption({}, optionAt(option.value))
    : undefined;

  const shared = {
    data,
    disabled: disabled ?? props.disabled,
    placeholder: props.placeholder,
    size,
    error: !!field.state.meta.errors.length && field.state.meta.isTouched,
    searchable: true,
    clearable: !disableClearable,
    autoFocus,
    filter,
    renderOption: renderOptionMantine,
    leftSection: autocompleteShowSearchIcon ? (
      <SvgIcon name="SearchSm" strokeWidth={1.6} />
    ) : undefined,
    rightSection: loading || loadingProp ? <Loader size="xs" /> : undefined,
    style: sx?.minWidth ? { minWidth: sx.minWidth, ...style } : style,
    ref: inputRef,
    ...(comboboxProps !== undefined ? { comboboxProps } : {}),
    ...(inputValue !== undefined
      ? {
          searchValue: inputValue,
          onSearchChange: (value: string) => onInputChange?.(null, value),
        }
      : {}),
    ...(open !== undefined ? { dropdownOpened: open } : {}),
    ...(openOnFocus !== undefined ? { openOnFocus } : {}),
    onDropdownOpen: onOpen,
    onDropdownClose: () => {
      if (suppressCloseAfterSelectRef.current) {
        suppressCloseAfterSelectRef.current = false;
        return;
      }
      onClose?.();
    },
  };

  return (
    <FieldWrapper field={field} {...props}>
      {(fieldId) =>
        multiple ? (
          <MultiSelect
            id={fieldId}
            {...shared}
            placeholder={countPlaceholder(selectedCount, props.placeholder)}
            value={((currentValue as AutocompleteOption[]) || [])
              // Selections with no matching option (stale persisted prefs,
              // options not loaded yet) must be dropped, not mapped to "-1":
              // Mantine echoes the value back through onChange, where
              // options[-1] → undefined would poison the form value (and any
              // storage it is persisted to, as null — INS-1753).
              .filter((value) => value != null)
              .map((value) =>
                options.findIndex((option: AutocompleteOption) =>
                  isEqual(option, value),
                ),
              )
              .filter((index) => index >= 0)
              .map(keyOf)}
            onChange={(keys) =>
              handleChange(
                keys
                  .map(optionAt)
                  .filter(
                    (option): option is AutocompleteOption => option != null,
                  ),
              )
            }
            onBlur={handleBlur}
            hidePickedOptions={false}
            styles={renderTags ? { pill: { display: "none" } } : undefined}
          />
        ) : (
          <Select
            id={fieldId}
            {...shared}
            value={(() => {
              if (currentValue == null || currentValue === "") return null;
              const index = options.findIndex((option: AutocompleteOption) =>
                isEqual(option, currentValue),
              );
              return index >= 0 ? keyOf(index) : null;
            })()}
            onChange={(key) => handleChange(optionAt(key))}
            onBlur={handleBlur}
          />
        )
      }
    </FieldWrapper>
  );
}

// Date picker component using context
type DatePickerProps = {
  datePickerInputProps?: Partial<DatePickerInputProps>;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  excludeDate?: (date: string) => boolean;
} & FieldBase;

function DatePickerField({
  datePickerInputProps,
  disableFuture,
  disablePast,
  format,
  maxDate,
  minDate,
  excludeDate,
  ...props
}: DatePickerProps) {
  const field = useFieldContext<string>();
  const fieldId = useId();
  const limits = getDateLimits({ disableFuture, disablePast });

  // dayjs's customParseFormat is stricter than Moment's forgiving parser:
  // fall back to the default parser for values that don't match the
  // DateFormatServer.SHORT contract, preserving Moment's behavior.
  const parseFieldValue = (value: string) => {
    const strict = dayjs(value, DateFormatServer.SHORT);
    return strict.isValid() ? strict : dayjs(value);
  };

  return (
    <DatePickerInput
      id={fieldId}
      value={
        field.state.value ? parseFieldValue(field.state.value).toDate() : null
      }
      onChange={(date) => {
        if (date) {
          field.handleChange(dayjs(date).format(DateFormatServer.SHORT));
        } else {
          field.handleChange("");
        }
      }}
      onBlur={field.handleBlur}
      error={
        field.state.meta.isTouched
          ? getErrorMessage(field.state.meta.errors)
          : undefined
      }
      disabled={props.disabled}
      placeholder={props.placeholder}
      label={props.label}
      description={props.helperText as string}
      minDate={latest(minDate, limits.minDate)}
      maxDate={earliest(maxDate, limits.maxDate)}
      excludeDate={excludeDate}
      valueFormat={format || DateFormat.SHORT}
      clearable
      {...datePickerInputProps}
    />
  );
}

// Password component using context
function PasswordField(props: FieldBase) {
  const field = useFieldContext<string>();

  return (
    <FieldWrapper field={field} {...props}>
      {(fieldId) => (
        <PasswordInput
          id={fieldId}
          value={field.state.value}
          onChange={(e) => field.handleChange(e.currentTarget.value)}
          onBlur={field.handleBlur}
          placeholder={props.placeholder}
          disabled={props.disabled}
          error={!!field.state.meta.errors.length && field.state.meta.isTouched}
          visibilityToggleIcon={({ reveal }) =>
            reveal ? (
              <SvgIconRaw name="Eye" size={20} />
            ) : (
              <SvgIconRaw name="EyeOff" size={20} />
            )
          }
        />
      )}
    </FieldWrapper>
  );
}

// Mantine Select component using context
type SelectFieldProps = {
  selectProps?: Omit<
    SelectProps,
    "value" | "onChange" | "onBlur" | "error" | "label" | "placeholder" | "data"
  >;
  data: SelectProps["data"];
} & FieldBase;

function SelectField({ selectProps, data, ...props }: SelectFieldProps) {
  const field = useFieldContext<string>();

  return (
    <FieldWrapper field={field} {...props}>
      {(fieldId) => (
        <Select
          id={fieldId}
          data={data}
          disabled={props.disabled}
          value={field.state.value || null}
          onChange={(value) => field.handleChange(value ?? "")}
          onBlur={field.handleBlur}
          placeholder={props.placeholder}
          error={!!field.state.meta.errors.length && field.state.meta.isTouched}
          {...selectProps}
        />
      )}
    </FieldWrapper>
  );
}

// Text component using context
type TextProps = {
  type?: "number" | "text";
  inputProps?: Omit<
    TextInputProps,
    "value" | "onChange" | "onBlur" | "error"
  > & {
    // MUI OutlinedInput compat: multiline renders a Textarea.
    multiline?: boolean;
  };
} & FieldBase;

function TextFieldComponent({
  type = "text",
  inputProps = {},
  ...props
}: TextProps) {
  const field = useFieldContext<string>();
  const { multiline, ...restInputProps } = inputProps;

  return (
    <FieldWrapper field={field} {...props}>
      {(fieldId) => {
        const shared = {
          id: fieldId,
          disabled: props.disabled,
          value: field.state.value,
          onChange: (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => field.handleChange(e.currentTarget.value),
          onBlur: field.handleBlur,
          placeholder: props.placeholder,
          error: !!field.state.meta.errors.length && field.state.meta.isTouched,
        };
        return multiline ? (
          <Textarea {...shared} {...(restInputProps as TextareaProps)} />
        ) : (
          <TextInput {...shared} type={type} {...restInputProps} />
        );
      }}
    </FieldWrapper>
  );
}

// Mantine TextInput component using context
type TextFieldMantineProps = {
  type?: "text" | "number" | "email" | "tel" | "url";
  inputProps?: Omit<TextInputProps, "value" | "onChange" | "onBlur" | "error">;
  disabled?: boolean;
  description?: string;
  label?: string;
  placeholder?: string;
  withAsterisk?: boolean;
};

function TextFieldMantine({
  type = "text",
  inputProps = {},
  ...props
}: TextFieldMantineProps) {
  const field = useFieldContext<string>();
  const fieldId = useId();

  return (
    <TextInput
      id={fieldId}
      value={field.state.value}
      type={type}
      onChange={(e) => field.handleChange(e.currentTarget.value)}
      onBlur={field.handleBlur}
      error={
        field.state.meta.isTouched
          ? getErrorMessage(field.state.meta.errors)
          : undefined
      }
      disabled={props.disabled}
      placeholder={props.placeholder}
      label={props.label}
      description={props.description}
      withAsterisk={props.withAsterisk}
      {...inputProps}
    />
  );
}

// Mantine Switch component using context
type SwitchFieldProps = {
  switchProps?: Omit<SwitchProps, "checked" | "onChange" | "onBlur" | "error">;
  disabled?: boolean;
  description?: string;
  label?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  color?: string;
  radius?: "xs" | "sm" | "md" | "lg" | "xl";
  labelPosition?: "left" | "right";
  offLabel?: React.ReactNode;
  onLabel?: React.ReactNode;
  tooltip?: string | TooltipProps;
};

function SwitchField({ switchProps = {}, ...props }: SwitchFieldProps) {
  const field = useFieldContext<boolean>();
  const fieldId = useId();

  const switchElement = (
    <Switch
      id={fieldId}
      checked={field.state.value || false}
      onChange={(e) => field.handleChange(e.currentTarget.checked)}
      onBlur={field.handleBlur}
      error={
        field.state.meta.isTouched
          ? getErrorMessage(field.state.meta.errors)
          : undefined
      }
      disabled={props.disabled}
      label={props.label}
      description={props.description}
      size={props.size}
      color={props.color}
      radius={props.radius}
      labelPosition={props.labelPosition}
      offLabel={props.offLabel}
      onLabel={props.onLabel}
      {...switchProps}
    />
  );

  if (props.tooltip) {
    const tooltipProps =
      typeof props.tooltip === "string"
        ? { label: props.tooltip }
        : props.tooltip;

    return (
      <Tooltip {...tooltipProps} refProp="rootRef">
        {switchElement}
      </Tooltip>
    );
  }

  return switchElement;
}

export {
  AutocompleteField,
  DatePickerField,
  PasswordField,
  SelectField,
  TextFieldComponent,
  TextFieldMantine,
  SwitchField,
};
